import { useState } from "react";
import { PRESCRIPTION_ACCEPTED_FILE_TYPES } from "@akina/domain";
import { useUpdatePrescription, type SharedFormatMessage } from "@akina/infra";
import { convertPdfToImage, getInitials, formatFullName } from "@akina/utils";
import { toast } from "sonner";
import { Button, Headline, SpacingX, Text } from "../atoms";
import { Avatar } from "../molecules";
import { UploadPrescriptionDropzone } from "./UploadPrescriptionDropzone";

const isAcceptedPrescriptionType = (type: string): boolean =>
  PRESCRIPTION_ACCEPTED_FILE_TYPES.includes(type as (typeof PRESCRIPTION_ACCEPTED_FILE_TYPES)[number]);

const noop = () => {};

type UploadPrescriptionModalContentProps = {
  formatMessage: SharedFormatMessage;
  onOpenChange: (open: boolean) => void;
  treatment: {
    treatmentId: string;
    firstName: string;
    lastName: string;
    profilePictureUrl?: string | null;
    age?: number | null;
  };
  app: "care" | "portal";
  onUploadSuccess?: () => void | Promise<void>;
};

export function UploadPrescriptionModalContent(props: UploadPrescriptionModalContentProps) {
  const { formatMessage, onOpenChange, treatment, app, onUploadSuccess } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [hadError, setHadError] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  const { mutateAsync: updatePrescription, isPending } = useUpdatePrescription(
    treatment.treatmentId,
    app,
  );

  const file = files[0];
  const canSave =
    file !== undefined &&
    isAcceptedPrescriptionType(file.type) &&
    !isPending &&
    !isConverting;

  const onSave = async () => {
    if (!file || !canSave) return;
    setIsConverting(true);
    let fileToUpload: File = file;
    if (file.type === "application/pdf") {
      try {
        fileToUpload = await convertPdfToImage(file);
      } catch (conversionError) {
        console.error("Prescription PDF conversion failed", conversionError);
        toast.error(
          formatMessage(
            { id: "shared.prescription.upload.errorToast" },
            { patientName: formatFullName(treatment) },
          ),
        );
        setIsConverting(false);
        return;
      }
    }
    try {
      await updatePrescription(fileToUpload);
    } catch {
      toast.error(
        formatMessage(
          { id: "shared.prescription.upload.errorToast" },
          { patientName: formatFullName(treatment) },
        ),
      );
      setIsConverting(false);
      return;
    }
    if (onUploadSuccess) {
      try {
        await onUploadSuccess();
      } catch (callbackError) {
        console.error("Upload success callback failed", callbackError);
        setIsConverting(false);
      }
    }
    onOpenChange(false);
    toast.success(
      formatMessage(
        { id: "shared.prescription.upload.successToast" },
        { patientName: formatFullName(treatment) },
      ),
    );
    setIsConverting(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex">
        <Headline size="2xl" className="mb-6">
          {formatMessage({ id: "shared.prescription.upload.title" })}
        </Headline>
        <SpacingX level={10} />
      </div>

      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-4">
          <Avatar
            size="h-20 w-20"
            initials={getInitials([treatment.firstName, treatment.lastName])}
            image={treatment.profilePictureUrl}
            colorTheme="primary"
            textSize="3xl"
          />
          <div className="flex flex-col items-start">
            <Text className="text-black">{formatFullName(treatment)}</Text>
            {treatment.age != null ? (
              <Text size="sm" className="text-gray-800/70">
                {formatMessage(
                  { id: "shared.prescription.upload.patientAge" },
                  { age: treatment.age },
                )}
              </Text>
            ) : null}
          </div>
        </div>
      </div>

      <UploadPrescriptionDropzone
        formatMessage={formatMessage}
        files={files}
        setFiles={setFiles}
        setIsDropActive={noop}
        hadError={hadError}
        setHadError={setHadError}
      />

      <Button onClick={onSave} disabled={!canSave} loading={isPending || isConverting}>
        {formatMessage({ id: "shared.prescription.upload.saveButton" })}
      </Button>
    </div>
  );
};
